import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactPlayer from "react-player";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { SplitText } from "gsap/SplitText";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";

import ButtonPrimary from "components/buttons/ButtonPrimary.js";
import MockupDisplay from "./other/landingPage/MockupDisplay";
import HidingMenuPublic from "./menu/HidingMenuPublic";
import Testimonial from "./other/landingPage/Testimonial";

import heroBackground from "assets/img/landingPage/hero-bg.svg";
import circlePointGreenIcon from "assets/img/icons/icons8-circle-point-green.svg";
import phoneMockup1 from "assets/img/landingPage/phone-mockup-1.png";
import videoCover from "assets/img/cover-1.jpg";
import raquelProfileImage from "assets/img/landingPage/testimonials/raquel.jpg";
import andyProfileImage from "assets/img/landingPage/testimonials/andy.jpg";
import olwamProfileImage from "assets/img/landingPage/testimonials/olwam.jpg";
import olwamPostImage from "assets/img/landingPage/testimonials/olwam-post.jpg";
import chrisPostImage from "assets/img/landingPage/testimonials/chris-post.jpg";
import chrisProfileImage from "assets/img/landingPage/testimonials/chris.jpg";
import joanieProfileImage from "assets/img/landingPage/testimonials/joanie.jpg";
import joaniePostImage from "assets/img/landingPage/testimonials/joanie-post.jpg";
import nancyProfileImage from "assets/img/landingPage/testimonials/nancy.jpg";
import nancyPostImage from "assets/img/landingPage/testimonials/nancy-post.jpg";
import scalelupLogo from "assets/img/logo/scalelup.svg";

// Actions
import { openModal } from "store/actions";

const LandingPage = styled.div`
  position: relative;
  width: 100%;

  font-family: "Montserrat", sans-serif;

  background-image: url(${heroBackground});
  background-repeat: no-repeat;
  background-color: #0a151f;
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100vh;

  background-size: 100% 100%;
  background-position: 0% 0%, 0% 0%;
  background-image: radial-gradient(
      45% 56% at 35% 21%,
      #5d56dd33 0%,
      #ff000000 99%
    ),
    radial-gradient(25% 39% at 19% 53%, #6c66d91a 0%, #ff000000 99%);
`;

const NarrowContent = styled.div`
  position: relative;
  width: 100%;

  max-width: 1300px;
  margin: 0 auto;
  padding: 0 40px;

  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 0 20px;
  }
`;

const Hero = styled.div`
  width: 100%;

  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const Colorful = styled.span`
  background: linear-gradient(
    270deg,
    #4891ff 13.89%,
    #8783d1 36.39%,
    #5dd39e 59.2%,
    #ffe70d 88.89%
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const ShortHeading = styled.h4`
  margin-top: ${(props) => (props.margintop ? props.margintop : "14vh")};
  margin-bottom: 0;
  padding: 0 60px;
  height: 32px;

  font-family: "Barlow";

  background: ${css.BRAND_INDIGO};
  border-radius: 14px;

  font-weight: 500;
  line-height: 1;

  display: flex;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    margin-top: ${(props) => (props.margintop ? props.margintop : "12vh")};
  }
`;

const MainHeading = styled.h1`
  margin-top: 20px;

  font-size: 52px;
  font-weight: 600;
  line-height: 115%;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 34px;
    text-align: left;
  }
`;

const HeroColumns = styled.div`
  width: 100%;
  margin-top: 20px;

  display: flex;
  flex-direction: row;
  gap: 80px;

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-top: 10px;

    align-items: flex-start;
    flex-direction: column;
  }
`;

const LeftContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ListHeading = styled.h1`
  margin-top: 6px;

  font-size: 26px;
  font-weight: 600;
  line-height: 130%;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 20px;
  }
`;

const List = styled.div`
  margin-top: 0px;
  padding-left: 20px;

  font-size: 18px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const ListItem = styled.div`
  display: flex;
  gap: 6px;
`;

const ListIcon = styled.div`
  width: 20px;
  min-width: 20px;
  height: 20px;
`;

const ListText = styled.div`
  line-height: 130%;
`;

const ShortHeadingRed = styled.h4`
  margin-top: 55px;
  padding: 0 60px;
  height: 32px;

  font-family: "Barlow";

  background: ${css.ERROR_RED};
  border-radius: 14px;

  font-weight: 500;
  line-height: 1;
  white-space: nowrap;

  display: flex;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    margin-top: 65px;
    padding: 0 30px;
  }
`;
const HeroParagraph = styled.div`
  font-size: 20px;
  line-height: 135%;
  color: ${css.TEXT_GREY};

  @media (max-width: ${globalConstants.switchMobile}) {
    text-align: left;
    font-size: 18px;
  }
`;

const HeroButtonWrap = styled.div`
  margin-top: 40px;
  margin-bottom: 0px;

  display: flex;
  justify-content: flex-start;

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-bottom: 50px;
  }
`;

const SecondContent = styled.div`
  padding-top: 20px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const MockupColumn = styled.div`
  min-width: 400px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-top: 36px;
    width: 100%;
    min-width: auto;
  }
`;

const RainbowBox = styled.div`
  position: relative;
  width: 100%;
  height: 500px;

  border-radius: ${css.BOX_RADIUS_PRIMARY};
  overflow: hidden;
`;

const RainbowBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 300%;
  height: 100%;

  background: linear-gradient(
    132deg,
    #8783d1 0.49%,
    #ffe70d 22.08%,
    #5dd39e 43.66%,
    #8783d1 65.24%,
    #5dd39e 86.82%,
    #ffe70d 108.4%
  );
`;

const PhoneBox = styled.div`
  position: absolute;
  top: 55px;
  left: 50%;
  width: 260px;

  transform: translateX(-50%);
`;

const SecretSection = styled.div`
  width: 100%;
  margin-top: 140px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    margin-top: 40px;
  }
`;

const SecretSectionTitle = styled.div`
  margin-top: 26px;

  font-size: 26px;
  font-weight: 600;
  line-height: 130%;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 20px;
  }
`;

const SecretSectionColumns = styled.div`
  width: 100%;
  margin-top: 10px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 80px;

  @media (max-width: ${globalConstants.switchTablet}) {
    flex-direction: column;
    gap: 50px;
  }
`;

const SecretSectionSecrets = styled.div`
  margin-top: 20px;
  width: 100%;
  max-width: 480px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const Secret = styled.div`
  display: flex;
  gap: 15px;

  @media (max-width: ${globalConstants.switchMobile}) {
    gap: 8px;
  }
`;

const SecretNumber = styled.div`
  min-width: 52px;

  font-size: 64px;
  font-weight: 500;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 52px;
  }
`;

const SecretText = styled.div`
  padding-top: 10px;

  line-height: 130%;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding-top: 5px;
  }
`;

const Bold = styled.span`
  font-weight: 500;
`;

const SecretVideo = styled.span`
  width: 100%;
  max-width: 640px;
  font-weight: 500;
`;

const VideoBox = styled.div`
  width: 100%;
  max-width: 640px;
  padding: 25px;
  box-sizing: border-box;
  aspect-ratio: 1.65;

  background: ${css.BRAND_BLUSH};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 40px;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 15px;

    flex-direction: column;
    align-items: flex-start;
  }
`;

const Testimonials = styled.div`
  margin-top: 140px;

  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 30px;

  @media (max-width: ${globalConstants.switchMobile}) {
    margin-top: 90px;
    align-items: flex-start;
  }
`;

const SubtitleParagraph = styled.div`
  margin-top: 0px;
  margin-bottom: ${(props) => (props.marginbottom ? props.marginbottom : "0")};
  max-width: 800px;

  font-size: 20px;
  line-height: 135%;
  color: ${css.TEXT_GREY};
  text-align: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    text-align: left;
  }
`;

const TestimonalsRow = styled.div`
  width: 100%;
  margin-top: 50px;

  overflow: hidden;
`;

const TestimonialsMarquue = styled.div`
  display: flex;
  gap: 30px;
`;

const TestimonialsGroup = styled.div`
  display: flex;
  gap: 30px;
`;

const Finale = styled.div`
  margin-top: 140px;
  margin-bottom: 40px;

  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 30px;

  @media (max-width: ${globalConstants.switchMobile}) {
    margin-top: 110px;
    margin-bottom: 80px;

    align-items: flex-start;
  }
`;

const FinaleTitle = styled.div`
  font-size: 45px;
  font-weight: 500;

  @media (max-width: ${globalConstants.switchMobile}) {
    font-size: 35px;
  }
`;

const FinaleBox = styled.div`
  width: 100%;
  max-width: 640px;
  padding: ${css.BOX_PADDING_LARGE};
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 40px;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 30px;

    flex-direction: column;
    align-items: flex-start;
  }
`;

const FinaleBoxLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const FinaleLogoWrap = styled.div`
  width: 170px;
`;

const FinaleSlogan = styled.div`
  max-width: 280px;

  font-size: 24px;
  font-weight: 400;
  line-height: 130%;
`;

const FinaleBoxRight = styled.div`
  max-width: 200px;

  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: ${globalConstants.switchMobile}) {
    width: 100%;
  }
`;

const FinaleRightText = styled.div`
  font-size: 18px;
  line-height: 130%;
`;

const FinaleButtonWrap = styled.div`
  font-family: "Barlow";
`;

export default () => {
  // set page Title
  useEffect(() => {
    document.title = "Scalelup | Drive Website Traffic, Build Awareness";
  }, []);

  const navigate = useNavigate();

  const UserProperties = createSelector(
    (state) => state.User,
    (user) => ({
      profileData: user.profileData,
    })
  );

  const { profileData } = useSelector(UserProperties);

  // useEffect(() => {
  //   if (profileData) navigate("/");
  // }, [profileData]);

  const container = useRef();
  const dispatch = useDispatch();

  gsap.registerPlugin(useGSAP, SplitText);
  const { contextSafe } = useGSAP({ scope: container });

  const tabletScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchTablet})`,
  });

  const mobileScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchMobile})`,
  });

  const openSignup = () => {
    if (!profileData) dispatch(openModal("signup"));
    else navigate("/home");
  };

  const text = `Are you ready to take your network marketing business to new heights? Scalelup is the dynamic platform designed to amplify your reach, enhance your brand visibility, and expand your network—all without spending a dime.`;

  const testimonials = (
    <TestimonialsGroup>
      <Testimonial
        name="Raquel"
        date="Dec 18, 2024"
        text="Discovering Scalelup has been a game-changer for my beauty business! As
        a home-based business owner, I struggled to expand my reach and find new
        reps. With Scalelup's free platform, I've seen a significant boost in
        sales and a growing team of passionate sellers."
        avatar={raquelProfileImage}
        username="Raquel Smith"
        postPublished="13w"
        postText={`💄 Unleash Your Inner Beauty Boss with Jeunesse! 💋

        Dreaming of a flexible career in the beauty industry? Join Jeunesse and transform your passion into a thriving business! 🌟
      
      ✨ Exclusive product line ✨ Comprehensive training ✨ Supportive community`}
      />
      <Testimonial
        white={true}
        name="Chris"
        date="Jan 3, 2025"
        text="Scalelup has been a phenomenal partner in growing our VPN affiliate program! After creating a post on their platform, we saw a significant influx of converting traffic to our website. Highly recommend it to anyone looking to boost their online presence!"
        avatar={chrisProfileImage}
        username="Chris Benson"
        postPublished="10w"
        postText={`Promote a top-rated VPN service trusted by millions for online privacy and security. Earn big 🤑🤑🤑`}
        postImage={chrisPostImage}
      />
      <Testimonial
        name="Olwam"
        date="Nov 5, 2024"
        text="Using Scalelup has taken my precious metals business to the next level! After posting about my opportunity on their platform, I received an overwhelming number of responses from interested prospects. This surge in engagement boosted my sales quickly. "
        avatar={olwamProfileImage}
        username="Olwam Shabangu"
        postPublished="16w"
        postText={`💲Join our precious metals club and transform your financial future! 👇`}
        postImage={olwamPostImage}
      />
      <Testimonial
        white={true}
        name="Andy"
        date="Nov 21, 2024"
        text="Partnering with Scalelup has transformed the way I operate my business! As someone eager to expand my network, this website has been invaluable. It has greatly increased my visibility, the difference in my business growth since joining is remarkable."
        avatar={andyProfileImage}
        username="Andy Raught"
        postPublished="11w"
        postText={`Join a leading financial services company with over 40 years of success, as a licensed Insurance Agent. Whether you're looking to earn extra income or build a full-time career, this entrepreneurial opportunity is perfect for you.

        ✅ Work remotely across the U.S.
        ✅ No quotas or income caps
        ✅ State & Federal licenses paid by us`}
      />
      <Testimonial
        name="Joanie"
        date="Jan 17, 2025"
        text="After using Scalelup to promote my offering, I saw a significant increase in enrollments and boosted my profits. The exposure provided was invaluable, connecting me to eager learners ready to invest in their future. The time spent on Scalelup has definitely been worthwhile."
        avatar={joanieProfileImage}
        username="Joanie Webb"
        postPublished="16w"
        postText={`💡 Unlock Money-Making Skills with Our Online Business & Marketing Course! Discover Powerful Strategies to Boost Revenue 💰`}
        postImage={joaniePostImage}
      />
      <Testimonial
        white={true}
        name="Nancy"
        date="Dec 5, 2024"
        text="I was initially skeptical about using Scalelup to promote my travel business, but it turned out to be one of the best decisions I've made! 🌟 After creating my first post, I was amazed by the influx of incoming messages. Scalelup gave me the exposure I needed to grow."
        avatar={nancyProfileImage}
        username="Nancy Winkler"
        postPublished="25d"
        postText={`Want to Turn Your Love for Travel into Steady Income? 🌍✈️🌍✈️🌍✈️
          
          Explore the world while earning money💵💵💵
           `}
        postImage={nancyPostImage}
      />
    </TestimonialsGroup>
  );

  const runTestimonials = contextSafe(() => {
    const testimonialsMarquee = document.getElementsByClassName(
      "testimonials-marquee"
    );

    const group = testimonialsMarquee[0].firstElementChild;

    const groupWidth =
      parseInt(getComputedStyle(group).getPropertyValue("width"), 10) + 30;

    let tl = gsap.timeline({ paused: true, repeat: -1 });

    tl.fromTo(
      testimonialsMarquee[0],
      { x: 0 },
      {
        x: -groupWidth,
        duration: 20,
        ease: "none",
      }
    );

    tl.play();

    if (!mobileScreen) {
      testimonialsMarquee[0].addEventListener("mouseenter", () => {
        gsap.to(tl, { timeScale: 0 });
      });
    } else {
      testimonialsMarquee[0].addEventListener("click", () => {
        gsap.to(tl, { timeScale: 0 });
      });
    }

    testimonialsMarquee[0].addEventListener("mouseleave", () => {
      gsap.to(tl, { timeScale: 1 });
    });

    // show whole box on hover
    const testimonials = document.querySelectorAll(".testimonial");

    const testimonialsElements = Array.from(testimonials);

    testimonials.forEach((testimonial) => {
      testimonial.addEventListener("mouseenter", (e) => {
        const pos = testimonial.getBoundingClientRect();

        if (pos.left < 0) {
          //scroll back to view the whole image
          const currentProgress = tl.progress();

          const toScrollBack = (1 / groupWidth) * pos.left * -1;

          const scrollToProgress = currentProgress - toScrollBack;

          gsap.to(tl, { progress: scrollToProgress });
        }

        const positionRight = window.innerWidth - pos.right;

        if (positionRight < 0) {
          //scroll forward to view the whole image
          const currentProgress = tl.progress();

          const toScrollForward = (1 / groupWidth) * positionRight * -1;

          const scrollToProgress = currentProgress + toScrollForward;

          gsap.to(tl, { progress: scrollToProgress });
        }

        const otherTestimonials = testimonialsElements.filter(
          (t) => t !== e.target
        );

        gsap.to(otherTestimonials, { opacity: 0.5 });
      });

      testimonial.addEventListener("mouseleave", () => {
        gsap.to(testimonialsElements, { opacity: 1 });
      });
    });
  });

  useEffect(() => {
    runTestimonials();
  }, []);

  return (
    <LandingPage ref={container}>
      <GradientOverlay />
      <NarrowContent>
        <Hero>
          <ShortHeading id="hero-short-heading">
            Build Your Home Business
          </ShortHeading>
          <MainHeading>
            <Colorful>Empower Your MLM Business</Colorful> with{" "}
            <u>Free&nbsp;Advertising</u> and Network Building
          </MainHeading>
          <HeroColumns>
            <LeftContent>
              <ListHeading>Do you want to...</ListHeading>
              <List>
                <ListItem>
                  <ListIcon>
                    <img src={circlePointGreenIcon} style={{ width: "100%" }} />
                  </ListIcon>
                  <ListText>
                    Get unlimited advertising opportunities for free?
                  </ListText>
                </ListItem>
                <ListItem>
                  <ListIcon>
                    <img src={circlePointGreenIcon} style={{ width: "100%" }} />
                  </ListIcon>
                  <ListText>Expand your business network?</ListText>
                </ListItem>
                <ListItem>
                  <ListIcon>
                    <img src={circlePointGreenIcon} style={{ width: "100%" }} />
                  </ListIcon>
                  <ListText>Generate MLM leads every day?</ListText>
                </ListItem>
                <ListItem>
                  <ListIcon>
                    <img src={circlePointGreenIcon} style={{ width: "100%" }} />
                  </ListIcon>
                  <ListText>
                    Increase your earnings without any expenses?
                  </ListText>
                </ListItem>
                <ListItem>
                  <ListIcon>
                    <img src={circlePointGreenIcon} style={{ width: "100%" }} />
                  </ListIcon>
                  <ListText>
                    Earn rewards while building your business?
                  </ListText>
                </ListItem>
              </List>

              {!tabletScreen && (
                <>
                  <ShortHeadingRed>
                    Show Your Offer to Thousands Every Day
                  </ShortHeadingRed>
                  <HeroParagraph>{text}</HeroParagraph>
                </>
              )}
              {!tabletScreen && (
                <HeroButtonWrap>
                  <ButtonPrimary onClick={openSignup}>
                    Create Free Account
                  </ButtonPrimary>
                </HeroButtonWrap>
              )}
            </LeftContent>
            {!tabletScreen && (
              <SecondContent>
                <MockupColumn>
                  <RainbowBox>
                    <RainbowBg />
                    <PhoneBox>
                      <img src={phoneMockup1} style={{ width: "100%" }} />
                      <MockupDisplay display="one" />
                    </PhoneBox>
                  </RainbowBox>
                </MockupColumn>
              </SecondContent>
            )}
          </HeroColumns>
        </Hero>
        {tabletScreen && (
          <SecondContent>
            <MockupColumn>
              <RainbowBox>
                <RainbowBg />
                <PhoneBox>
                  <img src={phoneMockup1} style={{ width: "100%" }} />
                  <MockupDisplay display="one" />
                </PhoneBox>
              </RainbowBox>
            </MockupColumn>
            <ShortHeadingRed>
              Show Your Offer to Thousands Every Day
            </ShortHeadingRed>
            <HeroParagraph>{text}</HeroParagraph>
          </SecondContent>
        )}
        {tabletScreen && (
          <HeroButtonWrap>
            <ButtonPrimary onClick={openSignup}>
              Create Free Account
            </ButtonPrimary>
          </HeroButtonWrap>
        )}
        <SecretSection>
          <ShortHeading margintop="0">What's The Secret</ShortHeading>
          <SecretSectionTitle>
            How Scalelup Works for MLM Success
          </SecretSectionTitle>
          <SecretSectionColumns>
            <SecretSectionSecrets>
              <Secret>
                <SecretNumber>1.</SecretNumber>
                <SecretText>
                  <Bold>Create Engaging Posts:</Bold> Use our user-friendly
                  interface to craft compelling messages and visuals that
                  highlight your business offerings. Include links and images to
                  drive interaction and interest.
                </SecretText>
              </Secret>
              <Secret>
                <SecretNumber>2.</SecretNumber>
                <SecretText>
                  <Bold>Earn Coins, Gain Visibility:</Bold> View posts from
                  other users to earn Coins, which you can then use to promote
                  your own content. This mutually beneficial system keeps you
                  active on the platform without incurring costs.
                </SecretText>
              </Secret>
              <Secret>
                <SecretNumber>3.</SecretNumber>
                <SecretText>
                  <Bold>Gain Traffic and Leads: </Bold> Use Scalelup's vast
                  network to drive traffic to your website and generate engaging
                  network marketing leads. With targeted exposure, convert views
                  into actionable business opportunities.
                </SecretText>
              </Secret>
              <Secret>
                <SecretNumber>4.</SecretNumber>
                <SecretText>
                  <Bold>Leverage Our Referral Program:</Bold> Share your unique
                  referral link to bring new members to Scalelup. As they engage
                  with the platform, earn Coins and cash bonuses, maximizing
                  your business's exposure.
                </SecretText>
              </Secret>
            </SecretSectionSecrets>
            <SecretVideo>
              <VideoBox>
                <div
                  style={{
                    position: "relative",
                    paddingTop: "56.25%",
                    width: "100%",
                  }}
                >
                  <iframe
                    src="https://iframe.mediadelivery.net/embed/420657/f2829f4c-0d9a-4750-ae1a-6f2442e876e5?autoplay=false&loop=true&muted=false&preload=false&responsive=true"
                    loading="lazy"
                    style={{
                      border: 0,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: "100%",
                    }}
                    allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                    allowfullscreen="true"
                  ></iframe>
                </div>
              </VideoBox>
            </SecretVideo>
          </SecretSectionColumns>
        </SecretSection>
        <Testimonials className="testimonials-section">
          <ShortHeading margintop="0">Success Stories</ShortHeading>
          <SubtitleParagraph>
            Hear from our users who have embraced Scalelup and learn how it has
            helped them achieve their home based business goals.
          </SubtitleParagraph>
        </Testimonials>
      </NarrowContent>
      <TestimonalsRow>
        <TestimonialsMarquue className="testimonials-marquee">
          {testimonials}
          {testimonials}
        </TestimonialsMarquue>
      </TestimonalsRow>
      <NarrowContent>
        <Finale>
          <ShortHeading margintop="0">Get Started Today</ShortHeading>
          <FinaleTitle>Ready to Supercharge Your Sales?</FinaleTitle>
          <FinaleBox>
            <FinaleBoxLeft>
              <FinaleLogoWrap>
                <img src={scalelupLogo} style={{ width: "100%" }} />
              </FinaleLogoWrap>
              <FinaleSlogan>
                Drive website traffic, build awareness!
              </FinaleSlogan>
            </FinaleBoxLeft>
            <FinaleBoxRight>
              <FinaleRightText>
                It takes 2 minutes and costs you nothing
              </FinaleRightText>
              <FinaleButtonWrap>
                <ButtonPrimary onClick={openSignup}>
                  Create Free Account
                </ButtonPrimary>
              </FinaleButtonWrap>
            </FinaleBoxRight>
          </FinaleBox>
        </Finale>
      </NarrowContent>
      <HidingMenuPublic />
    </LandingPage>
  );
};
